import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Command,
  Search,
  Save,
  X,
  Moon,
  FolderKanban,
  Tag,
  Bookmark,
  Clock,
  Settings,
  LogOut,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  shortcut?: string;
  action: () => void;
  category: 'action' | 'navigation' | 'selected';
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  commands: CommandItem[];
  selectedCount?: number;
}

export function CommandPalette({ isOpen, onClose, commands, selectedCount = 0 }: CommandPaletteProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter commands by search and group by category
  const { selectedCommands, actionCommands, navCommands, allCommands } = useMemo(() => {
    const filtered = commands.filter((cmd) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        cmd.label.toLowerCase().includes(q) ||
        (cmd.description?.toLowerCase().includes(q) ?? false)
      );
    });

    const selected = filtered.filter(c => c.category === 'selected');
    const actions = filtered.filter(c => c.category === 'action');
    const nav = filtered.filter(c => c.category === 'navigation');

    return {
      selectedCommands: selected,
      actionCommands: actions,
      navCommands: nav,
      allCommands: [...selected, ...actions, ...nav],
    };
  }, [commands, search]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, allCommands.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (allCommands[selectedIndex]) {
          allCommands[selectedIndex].action();
          onClose();
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [isOpen, allCommands, selectedIndex, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    const selected = listRef.current?.querySelector('[data-selected="true"]');
    selected?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-xs"
        onClick={onClose}
      />

      {/* Palette */}
      <div className="relative w-full max-w-lg bg-surface-primary border border-white/10 rounded-xl shadow-2xl overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
          <Search size={18} className="text-[#9a9aac]" />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('components.commandPalette.searchPlaceholder')}
            className="flex-1 bg-transparent text-white placeholder:text-[#9a9aac] outline-hidden text-sm"
          />
          <kbd className="hidden sm:inline-flex px-2 py-0.5 text-xs text-[#9a9aac] bg-surface-hover rounded">
            esc
          </kbd>
        </div>

        {/* Commands list */}
        <div ref={listRef} className="max-h-80 overflow-y-auto py-2">
          {selectedCount > 0 && selectedCommands.length > 0 && (
            <div className="px-3 py-1.5">
              <span className="text-xs font-medium text-[#9a9aac] uppercase tracking-wider">
                {t('components.commandPalette.selectedCount', { count: selectedCount })}
              </span>
            </div>
          )}
          {selectedCommands.map((cmd, i) => (
            <CommandRow
              key={cmd.id}
              command={cmd}
              isSelected={selectedIndex === i}
              onClick={() => { cmd.action(); onClose(); }}
            />
          ))}

          {actionCommands.length > 0 && (
            <div className="px-3 py-1.5 mt-1">
              <span className="text-xs font-medium text-[#9a9aac] uppercase tracking-wider">
                {t('components.commandPalette.actions')}
              </span>
            </div>
          )}
          {actionCommands.map((cmd, i) => (
            <CommandRow
              key={cmd.id}
              command={cmd}
              isSelected={selectedIndex === selectedCommands.length + i}
              onClick={() => { cmd.action(); onClose(); }}
            />
          ))}

          {navCommands.length > 0 && (
            <div className="px-3 py-1.5 mt-1">
              <span className="text-xs font-medium text-[#9a9aac] uppercase tracking-wider">
                {t('components.commandPalette.navigation')}
              </span>
            </div>
          )}
          {navCommands.map((cmd, i) => (
            <CommandRow
              key={cmd.id}
              command={cmd}
              isSelected={selectedIndex === selectedCommands.length + actionCommands.length + i}
              onClick={() => { cmd.action(); onClose(); }}
            />
          ))}

          {allCommands.length === 0 && (
            <div className="px-4 py-8 text-center text-[#9a9aac] text-sm">
              {t('components.commandPalette.noCommandsFound')}
            </div>
          )}
        </div>

        {/* Footer hints */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-white/5 text-xs text-[#9a9aac]">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-surface-hover rounded">↑</kbd>
            <kbd className="px-1.5 py-0.5 bg-surface-hover rounded">↓</kbd>
            {t('components.commandPalette.navigate')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-surface-hover rounded">↵</kbd>
            {t('components.commandPalette.select')}
          </span>
        </div>
      </div>
    </div>
  );
}

function CommandRow({
  command,
  isSelected,
  onClick
}: {
  command: CommandItem;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      data-selected={isSelected}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
        isSelected
          ? 'bg-brand-primary/10 text-white'
          : 'text-[#a0a0b0] hover:bg-surface-hover hover:text-white'
      }`}
    >
      <span className={isSelected ? 'text-brand-primary' : 'text-[#9a9aac]'}>
        {command.icon}
      </span>
      <span className="flex-1 text-sm">{command.label}</span>
      {command.shortcut && (
        <kbd className="px-2 py-0.5 text-xs bg-surface-hover rounded text-[#9a9aac]">
          {command.shortcut}
        </kbd>
      )}
    </button>
  );
}

// Export icon components for use in commands
export const CommandIcons = {
  Save: <Save size={16} />,
  Close: <X size={16} />,
  Snooze: <Moon size={16} />,
  Move: <FolderKanban size={16} />,
  Tag: <Tag size={16} />,
  Saved: <Bookmark size={16} />,
  Open: <Clock size={16} />,
  Settings: <Settings size={16} />,
  Logout: <LogOut size={16} />,
  Sync: <RefreshCw size={16} />,
  Command: <Command size={16} />,
};
